import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

const formatDiff = (change) => {
  if (change.to === 'updated' && change.from == null) return 'updated';
  const from = change.from == null || change.from === '' ? '—' : change.from;
  const to = change.to == null || change.to === '' ? '—' : change.to;
  return `${from} → ${to}`;
};

const sourceTagStyle = {
  fontSize: 11,
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 12,
  backgroundColor: 'var(--sl-bg-primary-alt)',
  color: 'var(--sl-text-secondary)',
};

function CrmEnrichmentLogCard({ teamId, organizationId, refreshKey }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!teamId || !organizationId) return undefined;

    let cancelled = false;
    const fetchLog = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await axios.get(
          `/teams/${teamId}/crm/organizations/${organizationId}/enrichment_log.json`,
        );
        if (!cancelled) setRuns((res.data && res.data.runs) || []);
      } catch (e) {
        if (!cancelled) setError('Failed to load enrichment history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchLog();
    return () => { cancelled = true; };
  }, [teamId, organizationId, refreshKey]);

  return (
    <div
      style={{
        border: '1px solid var(--sl-border-secondary)',
        borderRadius: 12,
        padding: 16,
        backgroundColor: 'var(--sl-bg-primary)',
      }}
    >
      <h5 style={{ marginBottom: 12, color: 'var(--sl-text-primary)' }}>Enrichment history</h5>

      {loading && <div style={{ color: 'var(--sl-text-secondary)' }}>Loading…</div>}
      {error && <div style={{ color: 'var(--sl-text-danger)' }}>{error}</div>}

      {!loading && !error && runs.length === 0 && (
        <div style={{ color: 'var(--sl-text-secondary)' }}>No enrichment history yet</div>
      )}

      {!loading && !error && runs.map((run, runIndex) => (
        <div
          key={`${run.date}-${run.time}-${runIndex}`}
          style={{
            paddingBottom: 12,
            marginBottom: 12,
            borderBottom: runIndex < runs.length - 1 ? '1px solid var(--sl-border-secondary)' : 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={sourceTagStyle}>{run.source}</span>
            <span style={{ fontSize: 12, color: 'var(--sl-text-secondary)' }}>
              {run.date} · {run.time}
            </span>
          </div>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {run.changes.map((change, changeIndex) => (
              <li key={`${change.field}-${changeIndex}`} style={{ fontSize: 13, color: 'var(--sl-text-primary)' }}>
                <strong>{change.field}:</strong> {formatDiff(change)}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

CrmEnrichmentLogCard.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  organizationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  refreshKey: PropTypes.number,
};

CrmEnrichmentLogCard.defaultProps = {
  refreshKey: 0,
};

export default CrmEnrichmentLogCard;
